/**
 * Form observer — tracks form start (focus/input) and complete (submit).
 * Uses MutationObserver for dynamic form discovery.
 *
 * A complete becomes `conversion_completed` server-side, so the selector is the
 * filter that decides what counts as a conversion. It used to fall back to
 * `"form"` when a trigger carried no selector, which meant "watch every form on
 * the site": on a Shopify storefront that turned search boxes, cart updates and
 * newsletter signups into 1,644 conversions in two days (Stag Shop, 2026-09-24).
 *
 * There is no fallback now. A trigger whose selector would match
 * indiscriminately is REFUSED here as well as at the API, because a tenant
 * configured before that validation existed still has the old row.
 */

import { watchSelector } from "./mutation";
import { formSelectorProblem } from "@bluehat/shared/triggers/form-selector";
import type { EventMessage } from "../messages";

type OnEvent = (msg: EventMessage) => void;

interface FormTriggerDef {
  id: string;
  selector?: string;
  mode: "start" | "complete" | "both";
}

export function setupFormObservers(
  triggers: FormTriggerDef[],
  onEvent: OnEvent,
): () => void {
  const startFired = new WeakSet<Element>();
  const triggerStartFired = new Set<string>();
  const cleanups: (() => void)[] = [];

  for (const trigger of triggers) {
    // No widening: an unusable selector means this trigger watches NOTHING.
    // Silent, because a visitor's page is not the place to report a
    // misconfiguration — the dashboard refuses it at the point of entry.
    const sel = trigger.selector;
    if (!sel || formSelectorProblem(sel)) continue;
    const trackStart = trigger.mode === "start" || trigger.mode === "both";
    const trackComplete = trigger.mode === "complete" || trigger.mode === "both";

    watchSelector(sel, (forms) => {
      for (const form of forms) {
        if (trackStart) {
          const onInteract = () => {
            if (startFired.has(form)) return;
            startFired.add(form);
            if (!triggerStartFired.has(trigger.id)) {
              triggerStartFired.add(trigger.id);
              onEvent({
                type: "event",
                event: "form_start",
                triggerId: trigger.id,
                formSelector: sel,
                url: window.location.href,
              });
            }
          };
          form.addEventListener("focusin", onInteract);
          form.addEventListener("input", onInteract);
          cleanups.push(() => {
            form.removeEventListener("focusin", onInteract);
            form.removeEventListener("input", onInteract);
          });
        }

        if (trackComplete) {
          const onSubmit = () => {
            onEvent({
              type: "event",
              event: "form_complete",
              triggerId: trigger.id,
              formSelector: sel,
              url: window.location.href,
            });
          };
          form.addEventListener("submit", onSubmit);
          cleanups.push(() => {
            form.removeEventListener("submit", onSubmit);
          });
        }
      }
    });
  }

  return () => {
    for (const fn of cleanups) fn();
  };
}
